import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import { useAppFilters } from '@/hooks';
import { useParams } from 'react-router-dom';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import { extractGraphConfigurations } from '@/modules/AssetsStudioPage/SensorFusion/utils/initialize-graphs';
import {
  createDataLayersConfig,
  DATA_LAYERS_RENDER_ORDER,
} from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Spectrum/config/data-layers-config';
import { useManagedGraphs } from '@/modules/AssetsStudioPage/SensorFusion/hooks';
import {
  createInitializationKey,
  initializeEnabledGraphs,
} from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Spectrum/utils/initialize-graphs';
import {
  useEnhancedQueryParams,
  useSensorQueryParams,
} from '@/modules/AssetsStudioPage/SensorFusion/hooks/use-sensor-query-params';
import { useVerticalLines } from '@/hooks/shared/DataLayers/use-vertical-lines';
import { createExpandableLegendFromFocus, toggleTimestampInSet } from '@/modules/shared/DataLayers/utils/createExpandableLegendFromFocus';
import { createExpandableLegendSeriesV2, toggleTimestampInExpandableLegend } from '@/modules/shared/DataLayers/utils/createExpandableLegendFromFocus.v2';
import type { ExpandableLegend, DataLayerWithExpandableLegend } from '@/types/ui/expandable-legend.types';
import { isExpandableLegendEnabled } from '@/config/featureFlags';
import { getExpandableLegendItems, getSelectedItemIds, toggleItemSelection, getCombinedItems, mergeExpandableLegendItemsWithVerticalLines } from '@/utils/expandableLegend.utils';

// Helper to compare two Sets by contents (avoids unnecessary re-renders from new Set references)
const areSetsEqual = (a: Set<string> | undefined, b: Set<string> | undefined): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
};

interface NewSpectrumProps {
  asset: any;
  components: any[];
  diagnostics: any[];
  config: any;
  activeOperationalModes: string[];
  activeDataLayers: string[];
  onLoadingStateChange?: (isLoading: boolean) => void;
  triggerRefresh?: number;
  sensorFusionVerticalLines?: any; // Read-only: vertical lines from SensorFusion for chips
  onRemoveSensorFusionMarker?: (markerId: string) => void; // Callback to remove marker from SensorFusion
  onClearAllSensorFusionMarkers?: () => void; // Callback to clear all user markers from SensorFusion
  selectedMarkerTimestamp?: string | null; // Lifted state: selected marker for highlighting
  setSelectedMarkerTimestamp?: (timestamp: string | null) => void; // Lifted state setter
  selectedTimestampsSet?: Set<string>; // Lifted state: selected timestamps in focus mode
  setSelectedTimestampsSet?: (timestamps: Set<string> | ((prev: Set<string>) => Set<string>)) => void; // Lifted state setter
  masterSensorColorMap?: Record<string, string>; // Global sensor name -> color mapping for consistency across all data layers
  spectrumVerticalLinesState?: ReturnType<typeof useVerticalLines>; // Spectrum's own vertical lines state from parent
  allowMultiTimestampMode?: boolean; // Control whether focus mode can select multiple timestamps (default: true)
}

export const NewSpectrum = ({
  asset,
  components,
  diagnostics,
  config,
  activeOperationalModes,
  activeDataLayers,
  onLoadingStateChange,
  triggerRefresh = 0,
  sensorFusionVerticalLines,
  onRemoveSensorFusionMarker,
  onClearAllSensorFusionMarkers,
  selectedMarkerTimestamp,
  setSelectedMarkerTimestamp,
  selectedTimestampsSet,
  setSelectedTimestampsSet,
  masterSensorColorMap,
  spectrumVerticalLinesState,
  allowMultiTimestampMode = true, // Default to true for backward compatibility
}: NewSpectrumProps) => {
  const { isMobileAsset } = useActiveAssetsType();
  const { siteId, id: assetId } = useParams();
  const assetName = asset?.asset_name;
  const { selectedComponent: selectedComponentId, selectedPart } = useAppFilters();
  const {
    dateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);
  const { selectedComponentObj } = useSelector((state: RootState) => state.assetsStudio);

  const [forceDataRefresh, setForceDataRefresh] = useState(0);

  // Force config refresh when filter options change (for expandable legend series)
  const [filterRefreshKey, setFilterRefreshKey] = useState(0);

  // Global loading sensors state - synchronized across all legends in all sections
  const [loadingSensors, setLoadingSensors] = useState<Set<string>>(new Set());

  // Track loading state for timestamps data (separate from sensor loading)
  const [isTimestampsLoading, setIsTimestampsLoading] = useState(false);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Ref to track latest selectedTimestampsSet for debounce (avoids stale closure)
  const selectedTimestampsSetRef = useRef<Set<string> | undefined>(selectedTimestampsSet);
  selectedTimestampsSetRef.current = selectedTimestampsSet;

  // Ref to track the last timestamp we triggered a fetch for (prevents duplicate fetches)
  const lastFetchedTimestampRef = useRef<string | string[] | undefined>(undefined);

  // Filter refresh callbacks (for UI updates)
  const debouncedFilterRefresh = useCallback(() => {
    // Just increment the key to refresh UI
    setFilterRefreshKey(prev => prev + 1);
  }, []);

  const immediateFilterRefresh = useCallback(() => {
    setFilterRefreshKey(prev => prev + 1);
  }, []);

  // Use vertical lines state from parent if provided, otherwise create local fallback
  const localVerticalLinesState = useVerticalLines();
  const verticalLinesState = spectrumVerticalLinesState || localVerticalLinesState;

  const { spectrumGraphs, spectrumAnalysisGraphs} = useMemo(() => {
    const extracted = extractGraphConfigurations(config);

    // Add splitToggle functionality to spectrum analysis graphs
    const spectrumAnalysisWithSplit = extracted.spectrumAnalysisGraphs.map((graph: any) => ({
      ...graph,
      splitToggle: true, // Enable split/merge toggle for all spectrum analysis graphs
    }));

    return {
      ...extracted,
      spectrumAnalysisGraphs: spectrumAnalysisWithSplit,
    };
  }, [config, selectedComponentId]);

  const selectedComponent = useMemo(
    () => components?.find((component) => component?.component_id === selectedComponentId),
    [components, selectedComponentId]
  );

  const selectedPartObj = selectedComponent?.subcomponents?.find((el: any) => el.subcomponent_id === selectedPart);

  const baseTitle = selectedPartObj
    ? selectedPartObj?.subcomponent_ui_name
    : selectedComponent?.component_ui_name || selectedComponent?.component_name;

  const initializationKeyRef = useRef<string>('');

  const initialEnabled = useMemo(() => {
    const result = initializeEnabledGraphs(spectrumAnalysisGraphs);
    const initKey = createInitializationKey(selectedComponentId, selectedPart, spectrumAnalysisGraphs);

    if (initKey !== initializationKeyRef.current) {
      initializationKeyRef.current = initKey;
    }

    return result;
  }, [spectrumAnalysisGraphs, selectedComponentId, selectedPart]);

  const spectrumManager = useManagedGraphs({
    graphs: spectrumGraphs,
    initialEnabled: initialEnabled,
    dataLayerKey: SENSOR_FUSION_DATA_LAYERS.SPECTRUM,
  });

  const spectrumAnalysisManager = useManagedGraphs({
    graphs: spectrumAnalysisGraphs,
    initialEnabled: initialEnabled,
    dataLayerKey: SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS,
  });

  const queryParams = useSensorQueryParams({
    siteId,
    assetId,
    assetName,
    selectedComponentId,
    selectedPart,
    isMobileAsset,
    from,
    to,
    isFullPull: false,
    isSpectral: true,
  });

  // Debounced selectedTimestampsSet for API calls (updates after 200ms of no changes)
  // Initialize as undefined so Latest is selected by default
  const [debouncedSelectedTimestampsSet, setDebouncedSelectedTimestampsSet] = useState<Set<string> | undefined>(
    selectedTimestampsSet
  );

  // Timestamp visibility state for chart (controls opacity, independent of selectedTimestampsSet)
  const [visibleTimestampsSet, setVisibleTimestampsSet] = useState<Set<string>>(new Set());

  // Auto-sync visible timestamps with selected timestamps
  // When a timestamp is checked in popover: add to both sets (sub-row appears + data visible)
  // When a timestamp is unchecked in popover: remove from both sets (sub-row disappears + data hidden)
  // When a timestamp is clicked in sub-row: only toggle visibility, not selection
  useEffect(() => {
    if (!selectedTimestampsSet) return;

    setVisibleTimestampsSet((prev) => {
      const newSet = new Set(prev);
      let changed = false;

      // Add any newly selected timestamps to visible set
      selectedTimestampsSet.forEach((timestampId) => {
        if (!newSet.has(timestampId)) {
          newSet.add(timestampId);
          changed = true;
        }
      });

      // Remove timestamps that are no longer in selected set
      Array.from(newSet).forEach((timestampId) => {
        if (!selectedTimestampsSet.has(timestampId)) {
          newSet.delete(timestampId);
          changed = true;
        }
      });

      return changed ? newSet : prev;
    });
  }, [selectedTimestampsSet]);

  // Simple legend sync mode toggle - container handles all state management
  const [legendSyncMode, setLegendSyncMode] = useState<'multi' | 'focus'>('multi');
  // Multi-section focus: map of sectionId -> focused sensor name
  const [focusedSensors, setFocusedSensors] = useState<Record<string, string>>({});

  // Ref to store allTimestampItems for access in handleFocus (defined early, updated later)
  const allTimestampItemsRef = useRef<any[]>([]);


  // Store sensor data from containers in state so it triggers re-renders
  const [registeredSensorData, setRegisteredSensorData] = useState<{
    enabledSensors: Record<string, Record<string, boolean>>;
    sensorColors: Record<string, Record<string, string>>;
  }>({
    enabledSensors: {},
    sensorColors: {},
  });

  // Function to register sensor data from containers
  const registerSensorData = useCallback(
    (dataLayer: string, enabledSensors: Record<string, boolean>, sensorColors: Record<string, string>) => {
      setRegisteredSensorData((prev) => ({
        enabledSensors: {
          ...prev.enabledSensors,
          [dataLayer]: enabledSensors,
        },
        sensorColors: {
          ...prev.sensorColors,
          [dataLayer]: sensorColors,
        },
      }));
    },
    []
  );

  // Debounce selectedTimestampsSet updates when multiple selections are made
  useEffect(() => {
    const currentSetSize = selectedTimestampsSet?.size || 0;

    // In focus mode, ALWAYS debounce to allow user to finish multi-selecting
    // In normal mode (single-select), update immediately for better UX
    if (legendSyncMode !== 'focus' && currentSetSize <= 1) {
      // Only update if contents actually changed (prevents unnecessary API refetches)
      setDebouncedSelectedTimestampsSet((prev) => {
        const newSet = new Set(selectedTimestampsSet || []);
        if (areSetsEqual(prev, newSet)) return prev; // Return same reference if contents unchanged
        return newSet;
      });
      return;
    }

    // In focus mode OR multiple selections, debounce the update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Use ref to get the LATEST value, avoiding stale closure issue
      // Only update if contents actually changed (prevents unnecessary API refetches)
      setDebouncedSelectedTimestampsSet((prev) => {
        const newSet = new Set(selectedTimestampsSetRef.current || []);
        if (areSetsEqual(prev, newSet)) return prev; // Return same reference if contents unchanged
        return newSet;
      });
    }, 200);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [selectedTimestampsSet, legendSyncMode]);

  // CRITICAL: Add selected timestamp(s) to query params for spectral data
  // Use selectedTimestampsSet directly - no debouncing for immediate response
  // NOTE: Fetch data for ALL selected timestamps, then control visibility client-side (visibleTimestampsSet)
  // NOTE: Never use '__latest__' - wait for a valid timestamp from parent
  const timestampParam = useMemo(() => {
    // Use selectedTimestampsSet directly for immediate response
    if (selectedTimestampsSet && selectedTimestampsSet.size > 0) {
      const allTimestamps = Array.from(selectedTimestampsSet);
      return allTimestamps.length === 1 ? allTimestamps[0] : allTimestamps;
    }
    // Fallback to single marker timestamp
    if (selectedMarkerTimestamp) {
      return selectedMarkerTimestamp;
    }
    // No timestamp yet - return undefined to prevent requests
    return undefined;
  }, [selectedTimestampsSet, selectedMarkerTimestamp]);

  // Increment forceDataRefresh when timestamp actually changes to trigger API refetch
  // This is needed because useFetchData's querySignature doesn't include snapshot_timestamp
  // Track last fetched timestamp to prevent duplicate fetches with the same timestamp
  useEffect(() => {
    // Skip if no timestamp yet
    if (timestampParam === undefined) return;

    // Compare with last fetched timestamp
    // IMPORTANT: Use spread to copy arrays before sorting - .sort() mutates in place!
    const currentTimestampKey = Array.isArray(timestampParam)
      ? [...timestampParam].sort().join(',')
      : timestampParam;
    const lastTimestampKey = Array.isArray(lastFetchedTimestampRef.current)
      ? [...lastFetchedTimestampRef.current].sort().join(',')
      : lastFetchedTimestampRef.current;

    // Skip if timestamp hasn't actually changed
    if (currentTimestampKey === lastTimestampKey) return;

    // Update last fetched timestamp
    lastFetchedTimestampRef.current = timestampParam;

    // Always increment forceDataRefresh when timestamp changes
    // The containers need this signal to refetch - they don't auto-fetch on mount
    setForceDataRefresh(prev => prev + 1);
  }, [timestampParam]);

  // CRITICAL: Memoize additionalParams so React detects changes properly
  // Only include snapshot_timestamp when we have a valid timestamp
  const additionalParams = useMemo(() => {
    const params: Record<string, any> = {
      data_layer: SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS,
    };
    // Only add snapshot_timestamp if we have a valid value
    if (timestampParam !== undefined) {
      params.snapshot_timestamp = timestampParam;
    }
    return params;
  }, [timestampParam]);

  const enhancedQueryParams = useEnhancedQueryParams(queryParams, additionalParams);

  const handleSensorLoadingStateChange = (isLoading: boolean) => {
    // Track timestamp loading state for chart loading indicator
    // This should apply in both focus mode AND single-select mode when switching timestamps
    setIsTimestampsLoading(isLoading);
  };

  const setTriggerSensorReset = (value: boolean) => {
    return;
  };

  // Save the enabled sensors state before entering focus mode
  // This will be populated by the container when focus is activated
  const savedEnabledSensorsRef = useRef<Record<string, Record<string, boolean>> | null>(null);

  const legendSync = useMemo(() => ({
    mode: legendSyncMode,
    toggleMode: () => {
      setLegendSyncMode(prev => {
        const newMode = prev === 'multi' ? 'focus' : 'multi';
        // Entering focus mode - always add Latest to the set
        if (newMode === 'focus' && setSelectedTimestampsSet) {
          const currentSet = selectedTimestampsSet || new Set();
          const newSet = new Set(currentSet);
          // Add all three possible Latest values to ensure Latest is selected
          newSet.add('__latest__');
          newSet.add('latest');
          newSet.add('0');
          setSelectedTimestampsSet(newSet);
        }
        // Exiting focus mode - return saved state to be restored
        if (newMode === 'multi') {
          setFocusedSensors({});
          // The container will use savedEnabledSensorsRef.current to restore
        }
        return newMode;
      });
    },
    // Expose saved state for restoration
    getSavedState: () => savedEnabledSensorsRef.current,
    // Clear saved state after restoration
    clearSavedState: () => { savedEnabledSensorsRef.current = null; },
  }), [legendSyncMode, selectedTimestampsSet, setSelectedTimestampsSet]);

  // Focus handler - switches to focus mode and sets the sensor with its section
  // currentEnabledSensors will be passed from the container
  const handleFocus = (sensorName: string, sectionId?: string, currentEnabledSensors?: Record<string, Record<string, boolean>>) => {
    // console.log('[handleFocus] Called with:', { sensorName, sectionId, currentFocusedSensors: focusedSensors });

    if (!sectionId) {
      // console.log('[handleFocus] No sectionId, returning');
      return;
    }

    // CRITICAL FIX: Check if this sensor is focused in ANY section (for synced legends)
    // When syncSectionLegends is true, the Focus badge appears on ALL sections showing the same sensor
    // So we need to check all sections to determine if we're toggling off
    const focusedInAnySection = Object.entries(focusedSensors).find(([_, name]) => name === sensorName);
    const isTogglingOff = focusedInAnySection !== undefined;
    // console.log('[handleFocus] focusedInAnySection:', focusedInAnySection, '| isTogglingOff:', isTogglingOff);

    if (isTogglingOff) {
      // User clicked Focus on the already-focused sensor - toggle off
      // CRITICAL FIX: Remove the sensor from the section where it's actually focused
      // Not necessarily the section where the badge was clicked (because of synced legends)
      const newFocusedSensors = { ...focusedSensors };
      if (focusedInAnySection) {
        const [actualSectionId] = focusedInAnySection;
        delete newFocusedSensors[actualSectionId];
      }

      setFocusedSensors(newFocusedSensors);

      // Check the NEW state (after deletion) to see if any sensors remain focused
      const remainingSections = Object.keys(newFocusedSensors);

      if (remainingSections.length === 0) {
        setLegendSyncMode('multi');
      }
      return;
    }

    // Save current enabled sensors if provided
    if (currentEnabledSensors) {
      savedEnabledSensorsRef.current = JSON.parse(JSON.stringify(currentEnabledSensors));
    }

    // Switch focus to the new sensor (even if another sensor was already focused)
    // console.log('[handleFocus] Setting new focus:', { sectionId, sensorName });
    setFocusedSensors(prev => {
      const newState = {
        ...prev,
        [sectionId]: sensorName,
      };
      // console.log('[handleFocus] New focusedSensors state:', newState);
      return newState;
    });

    // CRITICAL: Select ALL timestamps SYNCHRONOUSLY when entering focus mode
    // This ensures the API uses all timestamps immediately, before effects run
    const wasInFocusMode = legendSyncMode === 'focus';
    if (!wasInFocusMode && setSelectedTimestampsSet) {
      // Entering focus mode for the first time - select all timestamps NOW
      // Try ref first, then fallback to expandableLegend?.items directly
      let currentTimestampItems = allTimestampItemsRef.current;

      // Fallback: if ref is empty, try to get items from expandableLegend directly
      if (!currentTimestampItems || currentTimestampItems.length === 0) {
        const spectrumDataLayer = config?.dataLayers?.find(
          (layer: any) => layer.id === SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS
        );
        const legendItems = spectrumDataLayer?.expandableLegend?.items || config?.expandableLegend?.items || [];
        if (legendItems.length > 0) {
          currentTimestampItems = legendItems;
          // console.log('[handleFocus] Using fallback expandableLegend.items:', legendItems.length);
        }
      }

      // console.log('[handleFocus] Selecting all timestamps:', currentTimestampItems?.length || 0);

      if (currentTimestampItems && currentTimestampItems.length > 0) {
        const allItemIds = new Set<string>();
        const allItemIdsAndValues = new Set<string>();

        currentTimestampItems.forEach((item: any) => {
          const itemId = String(item.id);
          allItemIds.add(itemId);
          allItemIdsAndValues.add(itemId);
          if (item.value) {
            allItemIdsAndValues.add(item.value);
          }
        });

        // console.log('[handleFocus] Setting selectedTimestampsSet with:', Array.from(allItemIdsAndValues));
        setSelectedItemIds(allItemIds);
        setSelectedTimestampsSet(allItemIdsAndValues);
        setVisibleTimestampsSet(allItemIdsAndValues);

        // CRITICAL: Also update debouncedSelectedTimestampsSet IMMEDIATELY
        // This bypasses the 200ms debounce so the API gets all timestamps right away
        setDebouncedSelectedTimestampsSet(allItemIdsAndValues);
      }
    }

    setLegendSyncMode('focus');
  };

  // Derive focusMode from legendSyncMode
  const focusMode = legendSyncMode === 'focus';

  // DEBUG: Log focusMode state
  // console.log('[NewSpectrum] legendSyncMode:', legendSyncMode, '| focusMode:', focusMode);

  // CRITICAL: Force data refresh when focus changes to trigger refetch for the new sensor
  // Without this, returning to a previously focused sensor shows stale data
  const prevFocusedSensorRef = useRef<string | undefined>();
  useEffect(() => {
    const currentFocusedSensor = Object.values(focusedSensors)[0];
    if (focusMode && currentFocusedSensor && currentFocusedSensor !== prevFocusedSensorRef.current) {
      // console.log('[NewSpectrum] Focus changed, triggering data refresh:', prevFocusedSensorRef.current, '->', currentFocusedSensor);
      setForceDataRefresh(prev => prev + 1);
    }
    prevFocusedSensorRef.current = currentFocusedSensor;
  }, [focusedSensors, focusMode]);

  // CRITICAL: When exiting focus mode, keep only the first selected timestamp
  React.useEffect(() => {
    if (!focusMode && selectedTimestampsSet && selectedTimestampsSet.size > 1 && setSelectedTimestampsSet) {
      // Convert multi-select to single-select by keeping only the first timestamp
      const firstTimestamp = Array.from(selectedTimestampsSet)[0];
      setSelectedTimestampsSet(new Set([firstTimestamp]));
    }
  }, [focusMode]);

  // Track previous focus mode to detect transitions
  const prevFocusModeRef = useRef(focusMode);

  // Get expandable legend configuration from config
  const expandableLegend: ExpandableLegend | undefined = useMemo(() => {
    // Check multiple possible locations for expandable legend:

    // 1. At root level of config
    if (config?.expandableLegend?.enabled) {
      return config.expandableLegend;
    }

    // 2. In dataLayers for SPECTRUM_ANALYSIS
    const spectrumDataLayer = config?.dataLayers?.find(
      (layer: any) => layer.id === SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS
    ) as DataLayerWithExpandableLegend;

    if (spectrumDataLayer?.expandableLegend?.enabled) {
      return spectrumDataLayer.expandableLegend;
    }

    // 3. In any dataLayer
    const anyDataLayerWithLegend = config?.dataLayers?.find(
      (layer: any) => layer.expandableLegend?.enabled
    );
    if (anyDataLayerWithLegend?.expandableLegend) {
      return anyDataLayerWithLegend.expandableLegend;
    }

    return undefined;
  }, [config]);

  // State for selected timestamp item IDs (new approach)
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(() => {
    if (isExpandableLegendEnabled() && expandableLegend) {
      return new Set(expandableLegend.defaultSelection || []);
    }
    return new Set();
  });

  // Sync selectedItemIds with selectedTimestampsSet for highlighting on linked charts (SensorFusion)
  // NOTE: The sync of selectedItemIds -> selectedTimestampsSet is now handled by the
  // effect near allTimestampItems that uses the merged items (backend + user timestamps)
  // This ensures both sources are included in the selected timestamps.

  // Initialize selectedTimestampsSet from defaultSelection on mount (ONLY ONCE)
  // Use a ref to track if we've initialized, to prevent overriding focus mode selections
  const defaultSelectionInitializedRef = useRef(false);
  useEffect(() => {
    // CRITICAL: Skip if in focus mode - don't override the all-timestamps selection
    if (legendSyncMode === 'focus') {
      return;
    }

    // Skip if already initialized
    if (defaultSelectionInitializedRef.current) {
      return;
    }

    if (!isExpandableLegendEnabled() || !expandableLegend || !setSelectedTimestampsSet) {
      return;
    }

    const defaultSelection = expandableLegend.defaultSelection || [];
    if (defaultSelection.length === 0) {
      return;
    }

    // Mark as initialized
    defaultSelectionInitializedRef.current = true;

    // Convert default selection IDs to timestamp values
    // IMPORTANT: Include BOTH item ID and timestamp value so matching works by either
    const defaultTimestampValues = new Set<string>();
    defaultSelection.forEach((itemId: string) => {
      // Add the item ID itself (for line.id matching)
      defaultTimestampValues.add(itemId);

      const item = expandableLegend.items?.find((i: any) => i.id === itemId);
      if (item) {
        defaultTimestampValues.add(item.value); // Also add ISO timestamp value
      }
    });

    if (defaultTimestampValues.size > 0) {
      setSelectedTimestampsSet(defaultTimestampValues);
    }
  }, [expandableLegend?.defaultSelection, expandableLegend?.items, setSelectedTimestampsSet, legendSyncMode]);

  // Get all timestamp items (including linked sources - bidirectional sync)
  const allTimestampItems = useMemo(() => {
    const backendItems = expandableLegend?.items || [];
    const sensorFusionLines = sensorFusionVerticalLines?.verticalLines || [];

    // If no expandable legend, fall back to converting SensorFusion lines directly
    if (!expandableLegend) {
      // This maintains backward compatibility with the old approach
      return sensorFusionLines
        .filter((line: any) => !line.isHarmonic)
        .map((line: any) => ({
          id: line.id,
          value: line.timestamp instanceof Date ? line.timestamp.toISOString() : String(line.timestamp),
          label: line.label,
          enabled: true,
          source: 'user' as const,
          color: line.color || '#6667dd',
          deletable: true,
          createdAt: new Date().toISOString(),
        }));
    }

    // Merge backend items with SensorFusion user markers (bidirectional)
    const mergedItems = mergeExpandableLegendItemsWithVerticalLines(
      backendItems,
      sensorFusionLines,
      expandableLegend
    );

    return mergedItems;
  }, [expandableLegend, sensorFusionVerticalLines?.verticalLines]);

  // Update ref for handleFocus access
  allTimestampItemsRef.current = allTimestampItems;

  // CRITICAL: Sync selectedItemIds -> selectedTimestampsSet for API requests
  // This converts item IDs to timestamp VALUES that the API expects
  const prevTimestampValuesRef = useRef<string>('');
  useEffect(() => {
    if (!setSelectedTimestampsSet) return;

    // Convert selected item IDs to timestamp values
    const timestampValues = new Set<string>();
    selectedItemIds.forEach((itemId) => {
      // Add the item ID itself (for line.id matching)
      timestampValues.add(itemId);

      // Look up the item in merged items (backend + user timestamps)
      const item = allTimestampItems.find((i: any) => i.id === itemId);
      if (item && item.value) {
        timestampValues.add(item.value); // Add ISO timestamp value for API
      }
    });

    // Only update if the values actually changed (prevent infinite loops)
    const newValuesKey = Array.from(timestampValues).sort().join(',');
    if (newValuesKey !== prevTimestampValuesRef.current) {
      prevTimestampValuesRef.current = newValuesKey;
      setSelectedTimestampsSet(timestampValues);
    }
  }, [selectedItemIds, allTimestampItems, setSelectedTimestampsSet]);

  // Track whether we've initialized selection for current focus session
  const focusInitializedRef = useRef(false);

  // Reset initialization flag when exiting focus mode
  React.useEffect(() => {
    if (!focusMode) {
      focusInitializedRef.current = false;
    }
  }, [focusMode]);

  // CRITICAL: When entering focus mode, select ALL timestamps and show them as sub-legend items
  // This includes BOTH backend timestamps AND user-created timestamps (merged in allTimestampItems)
  React.useEffect(() => {
    // Detect ENTERING focus mode: transition from false -> true
    const isEnteringFocusMode = focusMode && !prevFocusModeRef.current;

    // console.log('[Focus Mode Entry Effect] focusMode:', focusMode, '| prevFocusMode:', prevFocusModeRef.current, '| isEnteringFocusMode:', isEnteringFocusMode);
    // console.log('[Focus Mode Entry Effect] focusInitializedRef:', focusInitializedRef.current);
    // console.log('[Focus Mode Entry Effect] allTimestampItems count:', allTimestampItems.length);

    // Update prev ref for next render
    prevFocusModeRef.current = focusMode;

    // Skip if not entering focus mode (either already in focus or exiting)
    if (!isEnteringFocusMode) {
      return;
    }

    // Use allTimestampItems which merges backend + user timestamps
    if (allTimestampItems.length > 0) {
      // Collect both IDs and VALUES for proper matching
      const allItemIds = new Set<string>();
      const allItemIdsAndValues = new Set<string>();

      allTimestampItems.forEach((item: any) => {
        const itemId = String(item.id);
        allItemIds.add(itemId);
        allItemIdsAndValues.add(itemId);
        if (item.value) {
          allItemIdsAndValues.add(item.value); // Also add ISO timestamp value
        }
      });

      // Debug log removed for performance

      // Mark as initialized BEFORE setting state to prevent re-runs
      focusInitializedRef.current = true;

      // Update selectedItemIds - controls sub-legend row visibility via 'enabled'
      setSelectedItemIds(allItemIds);

      // CRITICAL: Also update selectedTimestampsSet DIRECTLY to ensure API uses all timestamps
      // This avoids timing issues where the sync effect runs too late
      if (setSelectedTimestampsSet) {
        setSelectedTimestampsSet(allItemIdsAndValues);
        // Update the ref to prevent the sync effect from doing redundant work
        const newValuesKey = Array.from(allItemIdsAndValues).sort().join(',');
        prevTimestampValuesRef.current = newValuesKey;
      }

      // CRITICAL: Also update debouncedSelectedTimestampsSet IMMEDIATELY
      // This bypasses the 200ms debounce so the API gets all timestamps right away
      setDebouncedSelectedTimestampsSet(allItemIdsAndValues);

      // Update visible timestamps - controls chart visibility via 'visible'
      // Include both IDs and VALUES for proper line matching
      setVisibleTimestampsSet(allItemIdsAndValues);
    }
  }, [focusMode, allTimestampItems]);

  // CRITICAL: Auto-select ALL timestamps when allTimestampItems changes while in focus mode
  // This handles the case where items are loaded AFTER focus mode entry
  const prevTimestampItemsLengthRef = useRef(0);
  React.useEffect(() => {
    // Track length even when not in focus mode (for comparison later)
    if (!focusMode) {
      prevTimestampItemsLengthRef.current = allTimestampItems.length;
      return;
    }

    // In focus mode - check if allTimestampItems has MORE items than we've selected
    const currentLength = allTimestampItems.length;
    const prevLength = prevTimestampItemsLengthRef.current;

    // If items increased OR we have fewer selected than available, select ALL
    if (currentLength > selectedItemIds.size || currentLength > prevLength) {
      // Collect ALL items (not just new ones)
      const allItemIds = new Set<string>();
      const allItemIdsAndValues = new Set<string>();

      allTimestampItems.forEach((item: any) => {
        const itemId = String(item.id);
        allItemIds.add(itemId);
        allItemIdsAndValues.add(itemId);
        if (item.value) {
          allItemIdsAndValues.add(item.value);
        }
      });

      // Check if we need to update (avoid unnecessary updates)
      const needsUpdate = allItemIds.size > selectedItemIds.size ||
        !Array.from(allItemIds).every(id => selectedItemIds.has(id));

      if (needsUpdate) {
        // Select ALL items
        setSelectedItemIds(allItemIds);

        // CRITICAL: Also update selectedTimestampsSet DIRECTLY
        if (setSelectedTimestampsSet) {
          setSelectedTimestampsSet(allItemIdsAndValues);
          const newValuesKey = Array.from(allItemIdsAndValues).sort().join(',');
          prevTimestampValuesRef.current = newValuesKey;
        }

        // CRITICAL: Also update debouncedSelectedTimestampsSet IMMEDIATELY
        // This bypasses the 200ms debounce so the API gets all timestamps right away
        setDebouncedSelectedTimestampsSet(allItemIdsAndValues);

        // Update visible timestamps
        setVisibleTimestampsSet(allItemIdsAndValues);
      }
    }

    // Update the tracked length
    prevTimestampItemsLengthRef.current = currentLength;
  }, [focusMode, allTimestampItems, selectedItemIds]);

  // Create expandable legend series for SPECTRUM_ANALYSIS - sensor items with timestamp children (for rows)
  const spectrumExpandableLegendSeries = useMemo(() => {
    // When multi-timestamp mode is disabled, don't create expandable series (no sub-rows)
    if (!allowMultiTimestampMode) {
      return [];
    }

    const spectrumAnalysisEnabledSensors = registeredSensorData.enabledSensors[SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS] || {};
    const spectrumAnalysisSensorColors = registeredSensorData.sensorColors[SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS] || {};

    // Use new expandable legend approach if enabled
    if (isExpandableLegendEnabled() && expandableLegend) {
      return createExpandableLegendSeriesV2({
        expandableLegend,
        mergedItems: allTimestampItems, // Pass merged items (backend + user timestamps)
        selectedItemIds,
        visibleItemIds: visibleTimestampsSet,
        focusMode,
        focusedSensors,
        enabledSensors: spectrumAnalysisEnabledSensors,
        sensorColors: spectrumAnalysisSensorColors,
        timestampsLoading: isTimestampsLoading,
      });
    }

    // Fallback to old approach
    return createExpandableLegendFromFocus({
      focusMode,
      focusedSensors,
      selectedTimestampsSet: selectedTimestampsSet || new Set(),
      visibleTimestampsSet,
      verticalLines: sensorFusionVerticalLines?.verticalLines || [],
      sensorColors: spectrumAnalysisSensorColors,
      enabledSensors: spectrumAnalysisEnabledSensors,
      includeLatest: true, // Include Latest as a timestamp row
      timestampsLoading: isTimestampsLoading,
    });
  }, [allowMultiTimestampMode, focusMode, focusedSensors, selectedTimestampsSet, selectedItemIds, visibleTimestampsSet, sensorFusionVerticalLines?.verticalLines, registeredSensorData.enabledSensors, registeredSensorData.sensorColors, expandableLegend, allTimestampItems, isTimestampsLoading]);

  // Handler for timestamp sub-row toggle (controls chart visibility, NOT popover selection)
  const handleToggleExpandableTimestamp = useCallback((_seriesId: string, timestampId: string) => {
    if (isExpandableLegendEnabled() && expandableLegend) {
      // New approach: toggle both ID and value for proper matching
      setVisibleTimestampsSet((prev) => {
        const newSet = new Set(prev);

        // Find the timestamp item by ID or value
        const item = allTimestampItems.find((i: any) => i.id === timestampId || i.value === timestampId);
        const itemId = item ? item.id : timestampId;
        const itemValue = item?.value;

        // Check if currently visible (by ID or value)
        const isVisible = newSet.has(itemId) || (itemValue && newSet.has(itemValue));

        if (isVisible) {
          // Remove both ID and value
          newSet.delete(itemId);
          if (itemValue) newSet.delete(itemValue);
        } else {
          // Add both ID and value for proper matching
          newSet.add(itemId);
          if (itemValue) newSet.add(itemValue);
        }
        
        return newSet;
      });
    } else {
      // Fallback to old approach with "Latest" handling
      setVisibleTimestampsSet((prev) => {
        const newSet = new Set(prev);

        // Special handling for "Latest" - toggle all variations together
        const isLatest = timestampId === '__latest__' || timestampId === 'latest' || timestampId === '0';

        if (isLatest) {
          const hasLatest = newSet.has('__latest__') || newSet.has('latest') || newSet.has('0');
          if (hasLatest) {
            // Remove all Latest variations
            newSet.delete('__latest__');
            newSet.delete('latest');
            newSet.delete('0');
          } else {
            // Add all Latest variations
            newSet.add('__latest__');
            newSet.add('latest');
            newSet.add('0');
          }
        } else {
          // Regular timestamp toggle
          if (newSet.has(timestampId)) {
            newSet.delete(timestampId);
          } else {
            newSet.add(timestampId);
          }
        }

        return newSet;
      });
    }
  }, [expandableLegend, allTimestampItems]);

  // CRITICAL: useMemo with verticalLinesState.verticalLines as dependency
  // This ensures the config (including marker chips filter) updates when markers change
  const dataLayersConfig = useMemo(() => createDataLayersConfig({
    activeDataLayers,
    isMobileAsset,
    assetName,
    diagnostics,
    baseTitle,
    from,
    to,
    spectrumGraphs,
    spectrumAnalysisGraphs,
    spectrumManager,
    spectrumAnalysisManager,
    enhancedQueryParams,
    forceDataRefresh,
    config,
    handleSensorLoadingStateChange,
    selectedComponentId,
    selectedComponentObj,
    setTriggerSensorReset,
    handleOilLoadingStateChange: function (isLoading: boolean): void {
      throw new Error('Function not implemented.');
    },
    handleCoolantLoadingStateChange: function (isLoading: boolean): void {
      throw new Error('Function not implemented.');
    },
    // Legend sync - ONLY pass the mode, let container handle state
    legendSync,
    // Focus state and handler
    focusedSensors,
    onFocus: handleFocus,
    // Global loading sensors state - synchronized across all sections
    loadingSensors,
    setLoadingSensor: (sensorName: string) => {
      setLoadingSensors((prev) => new Set(prev).add(sensorName));
    },
    clearLoadingSensor: (sensorName: string) => {
      setLoadingSensors((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sensorName);
        return newSet;
      });
    },
    // Vertical lines state for Spectrum's own markers (on charts)
    verticalLinesState,
    // Callbacks to manage Spectrum's own markers
    onRemoveSpectrumMarker: (markerId: string) => {
      if (verticalLinesState?.removeVerticalLine) {
        verticalLinesState.removeVerticalLine(markerId);
      }
    },
    onClearAllSpectrumMarkers: () => {
      // Clear all user markers (non-harmonic markers) from Spectrum
      if (verticalLinesState?.verticalLines) {
        const userMarkers = verticalLinesState.verticalLines.filter(
          (line: any) => line.markerType === 'user' && !line.isHarmonic
        );
        userMarkers.forEach((marker: any) => {
          if (verticalLinesState?.removeVerticalLine) {
            verticalLinesState.removeVerticalLine(marker.id);
          }
        });
      }
    },
    // SensorFusion vertical lines for chips (read-only)
    sensorFusionVerticalLines,
    // Callback to remove SensorFusion marker
    onRemoveSensorFusionMarker,
    // Callback to clear all SensorFusion user markers
    onClearAllSensorFusionMarkers,
    // Selected marker for highlighting
    selectedMarkerTimestamp,
    setSelectedMarkerTimestamp,
    // Force refresh callbacks for filter changes
    forceFilterRefresh: immediateFilterRefresh,
    debouncedForceFilterRefresh: debouncedFilterRefresh,
    // Focus mode for multi-select (derived from legendSyncMode)
    focusMode: focusMode,
    setFocusMode: (newFocusMode: boolean) => {
      // Toggle legendSyncMode between 'focus' and 'multi' based on newFocusMode
      if (newFocusMode) {
        setLegendSyncMode('focus');
      } else {
        setLegendSyncMode('multi');
      }
    },
    // Sensor data registration
    registerSensorData,
    allEnabledSensors: registeredSensorData.enabledSensors,
    allSensorColors: registeredSensorData.sensorColors,
    masterSensorColorMap, // Global sensor color map for consistency across data layers
    // Selected timestamps in focus mode
    selectedTimestampsSet,
    setSelectedTimestampsSet,
    // Debounced selected timestamps - used for filter values and API calls
    debouncedSelectedTimestampsSet,
    // Expandable legend series for timestamp rows beneath focused sensors
    expandableLegendSeries: spectrumExpandableLegendSeries,
    onToggleExpandableTimestamp: handleToggleExpandableTimestamp,
    // Control whether focus mode allows multiple timestamp selection (with expandable sub-rows)
    allowMultiTimestampMode,
    // Loading state for timestamps data (used for chart loading in focus mode)
    isTimestampsLoading,
  }), [
    activeDataLayers,
    from,
    to,
    config,
    selectedComponentId,
    legendSyncMode, // Use the primitive state instead of the object
    focusedSensors, // React to focus changes
    // REMOVED: verticalLinesState?.verticalLines?.length - filter mutations handle this dynamically
    // REMOVED: sensorFusionVerticalLines?.verticalLines?.length - filter mutations handle this dynamically
    selectedMarkerTimestamp, // React to selected marker changes
    filterRefreshKey, // Force refresh when filter options change
    selectedTimestampsSet?.size, // React to timestamp selection changes (track size for Set changes)
    Array.from(selectedTimestampsSet || []).join(','), // Track actual timestamp values
    debouncedSelectedTimestampsSet?.size, // React to debounced timestamp changes
    Array.from(debouncedSelectedTimestampsSet || []).join(','), // Track debounced timestamp values
    spectrumExpandableLegendSeries, // React to expandable legend changes
    registerSensorData, // Stable callback
    handleToggleExpandableTimestamp, // Stable callback
    allowMultiTimestampMode, // React to changes in multi-timestamp mode setting
    isTimestampsLoading, // React to timestamps loading state changes
  ]);

  // Map configuration to component render map
  // IMPORTANT: Always run reduce so hooks inside layer.component() are always called
  // (component functions use useRef, useEffect, useMemo which must be called consistently)
  const dataLayersToComponent = dataLayersConfig.reduce((acc, layer) => {
    acc[layer.id] = layer.condition?.() ? layer.component() : null;
    return acc;
  }, {} as Record<string, React.ReactNode>);

  // Don't render containers until we have a valid timestamp
  // This prevents cancelled requests when timestamp arrives after initial render
  // IMPORTANT: Use conditional rendering AFTER reduce to ensure hooks are always called
  if (timestampParam === undefined) {
    return null;
  }

  return (
    <>
      {dataLayersToComponent[SENSOR_FUSION_DATA_LAYERS.AI_TREND]}
      <div className="flex flex-col gap-4 max-h-full">
        {DATA_LAYERS_RENDER_ORDER.filter((id) => id !== SENSOR_FUSION_DATA_LAYERS.AI_TREND)
          .map((id) =>
            dataLayersToComponent[id] ? <React.Fragment key={id}>{dataLayersToComponent[id]}</React.Fragment> : null
          )
          .filter(Boolean)}
      </div>
    </>
  );
};
